import React, { useState, useEffect } from 'react';
import { logUserActivityQuietly } from './supabaseClient';

export default function MethotrexateCalculator({ user }) {
  // --- STATE HOOKS FOR MODEL INPUTS ---
  const [samplingHour, setSamplingHour] = useState('48'); // '24', '48', '72', or '96'
  const [measuredMtxLevel, setMeasuredMtxLevel] = useState(''); // Plasma level in mcmol/L
  const [weight, setWeight] = useState(''); // kg
  const [height, setHeight] = useState(''); // cm

  // --- STATE HOOK FOR LIVE CLINICAL EVALUATION PANEL ---
  const [evaluation, setEvaluation] = useState({
    statusLabel: 'AWAITING METRICS',
    color: '#7f8c8d',
    bgColor: 'rgba(127, 140, 141, 0.1)',
    bsa: '0.00',
    leucovorinUnitDose: 0, // mg/m2
    leucovorinAbsoluteDose: '0.0', // Absolute mg QID
    actionPlan: 'Provide patient biometrics and blood assay values to generate clinical rescue insights.'
  });

  // Reusable Dark-Mode Layout Themes
  const cardStyle = { background: '#1c2226', padding: '20px', borderRadius: '10px', border: '1px solid #2c3539', marginBottom: '20px', boxSizing: 'border-box' };
  const labelStyle = { display: 'block', fontSize: '0.68rem', fontWeight: 'bold', color: '#95a5a6', marginBottom: '6px', letterSpacing: '0.3px' };
  const inputStyle = { width: '100%', padding: '10px', background: '#111416', border: '1px solid #34495e', borderRadius: '6px', color: '#fff', fontSize: '0.85rem', boxSizing: 'border-box' };

  useEffect(() => {
    const valMTX = parseFloat(measuredMtxLevel);
    const weightNum = parseFloat(weight);
    const heightNum = parseFloat(height);

    // 1. Calculate Body Surface Area using Mosteller Formula
    let bsaCalc = 0;
    if (weightNum > 0 && heightNum > 0) {
      bsaCalc = Math.sqrt((heightNum * weightNum) / 3600);
    }

    if (isNaN(valMTX) || valMTX <= 0 || bsaCalc === 0) {
      setEvaluation({
        statusLabel: 'AWAITING METRICS',
        color: '#7f8c8d',
        bgColor: 'rgba(127, 140, 141, 0.1)',
        bsa: bsaCalc.toFixed(2),
        leucovorinUnitDose: 0,
        leucovorinAbsoluteDose: '0.0',
        actionPlan: 'Provide patient biometrics and blood assay values to generate clinical rescue insights.'
      });
      return;
    }

    // --- 2. HANDBOOK CORE MATRIX LOOKUP ENGINE ---
    let unitDose = 0; // mg/m2
    let statusTxt = 'NORMAL ELIMINATION';
    let statusClr = '#2ecc71';
    let statusBg = 'rgba(46, 204, 113, 0.15)';
    let supportivePlan = '';

    if (samplingHour === '24') {
      if (valMTX < 10.1) {
        unitDose = 0; // Baseline normal
        statusTxt = 'NORMAL ELIMINATION TIMELINE';
        supportivePlan = 'MTX level is below high-alert toxicity risk limits (< 10.1 µmol/L). Follow standard institutional protocol guidelines.';
      } else if (valMTX >= 10.1 && valMTX <= 12.0) {
        unitDose = 90;
        statusTxt = '⚠️ WATCH RANGE DETECTED';
        statusClr = '#f1c40f';
        statusBg = 'rgba(241, 196, 15, 0.15)';
      } else if (valMTX >= 12.1 && valMTX <= 18.0) {
        unitDose = 150;
        statusTxt = '🚨 HIGH TOXIC ACCUMULATION DETECTED';
        statusClr = '#e67e22';
        statusBg = 'rgba(230, 126, 34, 0.15)';
      } else if (valMTX > 18.0) {
        unitDose = 300;
        statusTxt = '🔥 CRITICAL ULTRA-HIGH TOXIC ACCUMULATION';
        statusClr = '#e74c3c';
        statusBg = 'rgba(231, 76, 60, 0.15)';
      }
    } 
    
    else if (samplingHour === '48') {
      if (valMTX <= 1.0) {
        unitDose = 0;
        statusTxt = 'NORMAL ELIMINATION TIMELINE';
        supportivePlan = 'MTX level is safe at the 48-hour gate (≤ 1.0 µmol/L). Maintain hydration.';
      } else if (valMTX >= 1.1 && valMTX <= 1.8) {
        unitDose = 15;
        statusTxt = '⚠️ CHRONIC ACCUMULATION RISK';
        statusClr = '#f1c40f';
        statusBg = 'rgba(241, 196, 15, 0.15)';
      } else if (valMTX >= 1.9 && valMTX <= 2.8) {
        unitDose = 30;
        statusTxt = '🚨 ELEVATED CLEARANCE BLOCKADE';
        statusClr = '#e67e22';
        statusBg = 'rgba(230, 126, 34, 0.15)';
      } else if (valMTX >= 2.9 && valMTX <= 8.5) {
        unitDose = 90;
        statusTxt = '🚨 SEVERE RETENTION PATHWAY';
        statusClr = '#e67e22';
        statusBg = 'rgba(230, 126, 34, 0.15)';
      } else if (valMTX >= 8.6 && valMTX <= 18.0) {
        unitDose = 150;
        statusTxt = '🔥 CRITICAL EXCRETION CRASH';
        statusClr = '#e74c3c';
        statusBg = 'rgba(231, 76, 60, 0.15)';
      } else if (valMTX > 18.0) {
        unitDose = 300;
        statusTxt = '🔥 FULMINANT CITOTOXICITY BLOWOUT';
        statusClr = '#e74c3c';
        statusBg = 'rgba(231, 76, 60, 0.15)';
      }
    } 
    
    else if (samplingHour === '72' || samplingHour === '96') {
      // Post 96 hours references the same concentration target ranges as 72 hours per handbook guidelines
      if (valMTX < 0.1) {
        unitDose = 0;
        statusTxt = '🎯 ABSOLUTE TREATMENT CEILING PASSED';
        supportivePlan = 'MTX concentration successfully cleared below safety ceiling floor (< 0.1 µmol/L). Leucovorin rescue protocol may safely cease.';
      } else if (valMTX >= 0.1 && valMTX <= 0.29) {
        unitDose = 10;
        statusTxt = '⚠️ MINIMAL TOXIC RESIDUE';
        statusClr = '#f1c40f';
        statusBg = 'rgba(241, 196, 15, 0.15)';
      } else if (valMTX >= 0.3 && valMTX <= 1.8) {
        unitDose = 15;
        statusTxt = '⚠️ MODERATE RETAINED RESIDUE';
        statusClr = '#f1c40f';
        statusBg = 'rgba(241, 196, 15, 0.15)';
      } else if (valMTX >= 1.9 && valMTX <= 2.8) {
        unitDose = 30;
        statusTxt = '🚨 STRONG SYSTEMIC RETENTION';
        statusClr = '#e67e22';
        statusBg = 'rgba(230, 126, 34, 0.15)';
      } else if (valMTX >= 2.9 && valMTX <= 9.8) {
        unitDose = 90;
        statusTxt = '🚨 CRITICAL SYSTEMIC RETENTION';
        statusClr = '#e67e22';
        statusBg = 'rgba(230, 126, 34, 0.15)';
      } else if (valMTX >= 9.9 && valMTX <= 19.0) {
        unitDose = 150;
        statusTxt = '🔥 EXTREME PATHOLOGICAL RETENTION';
        statusClr = '#e74c3c';
        statusBg = 'rgba(231, 76, 60, 0.15)';
      } else if (valMTX > 19.0) {
        unitDose = 300;
        statusTxt = '🔥 FULMINANT RENAL ENZYME SYSTEM SHUTDOWN';
        statusClr = '#e74c3c';
        statusBg = 'rgba(231, 76, 60, 0.15)';
      }
    }

    // Set Supportive Hydration and sampling strategy rules based on severity
    if (unitDose > 0) {
      supportivePlan = `It is suggested to give IV Leucovorin ${evaluation.leucovorinAbsoluteDose} mg QID and resample a random MTX concentration after completing the initial 24 hours of intensified leucovorin rescue therapy. Ensure vigorous alkaline fluid hydration tracking (Urine pH ≥ 7.0).`;
    }

    // Compute absolute milligram total payload
    const absoluteMgPayload = unitDose * bsaCalc;

    setEvaluation({
      statusLabel: statusTxt,
      color: statusClr,
      bgColor: statusBg,
      bsa: bsaCalc.toFixed(2),
      leucovorinUnitDose: unitDose,
      leucovorinAbsoluteDose: absoluteMgPayload.toFixed(1),
      actionPlan: supportivePlan
    });

  }, [samplingHour, measuredMtxLevel, weight, height, evaluation.leucovorinAbsoluteDose]);

  return (
    <div style={{ padding: '20px', background: '#0e1013', minHeight: '100vh', color: '#ecf0f1', fontFamily: 'system-ui, -apple-system' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
        <button 
          type="button"
          className="no-print" 
          onClick={() => {
                          const actualUserEmail = user && user.email ? user.email : 'anonymous-user';
                          const trackingLabel = `${actualUserEmail}-printed-Methotrexate`;
                          logUserActivityQuietly(trackingLabel); 
                          window.print();
                      }}
          style={{
            padding: '10px 20px',
            backgroundColor: '#27ae60', 
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: 'bold',
            fontSize: '0.9rem',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.2)'
          }}
        >
          🖨️ Print PDF Report
        </button>
      </div>
      {/* HEADER BAR TITLE */}
      <div style={{ textAlign: 'center', marginBottom: '25px', borderBottom: '2px solid #e74c3c', paddingBottom: '15px' }}>
        <h2 style={{ margin: 0, fontSize: '1.8rem', color: '#e74c3c', fontWeight: '800' }}>METHOTREXATE TOXICITY TRIAGE</h2>
        <span style={{ fontSize: '0.8rem', color: '#95a5a6', textTransform: 'uppercase' }}>BSA-Calibrated Rescue & Rescue Optimization Dashboard</span>
      </div>
      {/* HANDBOOK DROPDOWNS WRAPPER CONTAINER */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
        
        {/* DROPDOWN 1: THERAPEUTIC TARGET RANGES (tdm10.png) */}
        <details style={{
          background: '#1c2226',
          border: '1px solid #34495e',
          borderRadius: '8px',
          padding: '12px',
          cursor: 'pointer'
        }}>
          <summary style={{ fontSize: '0.82rem', fontWeight: 'bold', color: '#e74c3c', outline: 'none', userSelect: 'none' }}>
            📊 View Acceptable Therapeutic Windows (Click to expand)
          </summary>
          
          <div style={{ marginTop: '12px', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '10px', cursor: 'default' }} onClick={(e) => e.stopPropagation()}>
            
            {/* HIGH-DOSE CRITERIA BLOCK */}
            <h5 style={{ color: '#e74c3c', margin: '0 0 8px 0', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
              Acceptable Ranges (High-Dose Methotrexate Pathway):
            </h5>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '10px', marginBottom: '15px' }}>
              <div style={{ background: '#111416', padding: '8px 12px', borderRadius: '4px' }}>
                <span style={{ fontSize: '0.62rem', color: '#7f8c8d', display: 'block' }}>POST 24 HOURS:</span>
                <strong style={{ fontSize: '0.85rem', color: '#fff' }}>5.0 – 10.0 <span style={{ fontSize: '0.65rem', color: '#7f8c8d', fontWeight: 'normal' }}>µmol/L</span></strong>
              </div>
              <div style={{ background: '#111416', padding: '8px 12px', borderRadius: '4px' }}>
                <span style={{ fontSize: '0.62rem', color: '#7f8c8d', display: 'block' }}>POST 48 HOURS:</span>
                <strong style={{ fontSize: '0.85rem', color: '#fff' }}>0.5 – 1.0 <span style={{ fontSize: '0.65rem', color: '#7f8c8d', fontWeight: 'normal' }}>µmol/L</span></strong>
              </div>
              <div style={{ background: '#111416', padding: '8px 12px', borderRadius: '4px' }}>
                <span style={{ fontSize: '0.62rem', color: '#7f8c8d', display: 'block' }}>POST 72 HOURS:</span>
                <strong style={{ fontSize: '0.85rem', color: '#2ecc71' }}>&lt; 0.1 <span style={{ fontSize: '0.65rem', color: '#7f8c8d', fontWeight: 'normal' }}>µmol/L</span></strong>
              </div>
              <div style={{ background: '#111416', padding: '8px 12px', borderRadius: '4px' }}>
                <span style={{ fontSize: '0.62rem', color: '#7f8c8d', display: 'block' }}>RANDOM TIME ASSAY:</span>
                <strong style={{ fontSize: '0.85rem', color: '#2ecc71' }}>&lt; 0.05 <span style={{ fontSize: '0.65rem', color: '#7f8c8d', fontWeight: 'normal' }}>µmol/L</span></strong>
                <span style={{ fontSize: '0.58rem', color: '#7f8c8d', display: 'block', marginTop: '2px' }}>(Undetectable)</span>
              </div>
            </div>

            {/* ORAL PATHWAY BLOCK */}
            <h5 style={{ color: '#3498db', margin: '0 0 8px 0', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
              Therapeutic Target Ranges for Oral Methotrexate:
            </h5>
            <div style={{ background: '#111416', padding: '10px', borderRadius: '6px', fontSize: '0.75rem', lineHeight: '1.5', color: '#bdc3c7' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '4px', borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                <span>• Rheumatoid Arthritis:</span>
                <strong style={{ color: '#fff' }}>&lt; 0.1 µmol/L <span style={{ fontWeight: 'normal', color: '#7f8c8d' }}>(Post 24 hours)</span></strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '4px' }}>
                <span>• Juvenile Rheumatoid Arthritis:</span>
                <div style={{ textAlign: 'right' }}>
                  <strong style={{ color: '#fff', display: 'block' }}>&gt; 0.06 µmol/L <span style={{ fontWeight: 'normal', color: '#7f8c8d' }}>(Post 1 hour)</span></strong>
                  <strong style={{ color: '#fff', display: 'block' }}>&lt; 0.01 µmol/L <span style={{ fontWeight: 'normal', color: '#7f8c8d' }}>(Post 24 hours)</span></strong>
                </div>
              </div>
            </div>

          </div>
        </details>

        {/* DROPDOWN 2: LEUCOVORIN ADMINISTRATION & STABILITY MATRIX (tdm1.png) */}
        <details style={{
          background: '#1c2226',
          border: '1px solid #34495e',
          borderRadius: '8px',
          padding: '12px',
          cursor: 'pointer'
        }}>
          <summary style={{ fontSize: '0.82rem', fontWeight: 'bold', color: '#9b59b6', outline: 'none', userSelect: 'none' }}>
            🧪 View Leucovorin Dilution, Administration & Stability Guide
          </summary>
          
          <div style={{ marginTop: '12px', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '10px', cursor: 'default' }} onClick={(e) => e.stopPropagation()}>
            
            {/* ADMINISTRATION INFO SPEC */}
            <div style={{ background: '#111416', padding: '12px', borderRadius: '6px', fontSize: '0.75rem', lineHeight: '1.5', color: '#bdc3c7', marginBottom: '12px' }}>
              <strong style={{ color: '#fff', display: 'block', marginBottom: '4px', fontSize: '0.78rem' }}>🔧 Compound Spec & Administration Protocol:</strong>
              • <strong>Vial Strength:</strong> 50 mg / 5 mL<br />
              • <strong>Dilution Target:</strong> Dilute required calculated dose into <strong>100 mL – 250 mL</strong> of acceptable diluent.<br />
              • <strong>Approved Diluents:</strong> Normal Saline (NS), D5, D10, NSD10, or Ringer's Solution.<br />
              • <strong>IV Infusion Rate:</strong> Infuse over <strong>15 minutes to 2 hours</strong> <span style={{ color: '#e74c3c', fontWeight: 'bold' }}>(Do NOT exceed 160 mg/min rate limit)</span>.
            </div>

            {/* STABILITY MATRIX DATA TABLE */}
            <strong style={{ color: '#fff', display: 'block', marginBottom: '6px', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
              ⏰ Reconstitution Solution Stability Grid:
            </strong>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.7rem', textAlign: 'left', background: '#111416', borderRadius: '6px', overflow: 'hidden' }}>
              <thead>
                <tr style={{ background: 'rgba(155, 89, 182, 0.15)', color: '#d6a2e8' }}>
                  <th style={{ padding: '8px', borderBottom: '1px solid #2c3539' }}>Approved Diluent Type</th>
                  <th style={{ padding: '8px', borderBottom: '1px solid #2c3539' }}>Room Temp (&lt; 25°C)</th>
                  <th style={{ padding: '8px', borderBottom: '1px solid #2c3539' }}>Refrigerator (2°–8°C)</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.02)' }}>
                  <td style={{ padding: '8px', fontWeight: 'bold', color: '#fff' }}>Normal Saline, Ringer's</td>
                  <td style={{ padding: '8px', color: '#f1c40f' }}>24 Hours</td>
                  <td style={{ padding: '8px', color: '#7f8c8d' }}>--</td>
                </tr>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.02)' }}>
                  <td style={{ padding: '8px', fontWeight: 'bold', color: '#fff' }}>D5, D10</td>
                  <td style={{ padding: '8px', color: '#f1c40f' }}>12 Hours</td>
                  <td style={{ padding: '8px', color: '#7f8c8d' }}>--</td>
                </tr>
                <tr>
                  <td style={{ padding: '8px', fontWeight: 'bold', color: '#fff' }}>NSD10</td>
                  <td style={{ padding: '8px', color: '#f1c40f' }}>6 Hours</td>
                  <td style={{ padding: '8px', color: '#7f8c8d' }}>--</td>
                </tr>
              </tbody>
            </table>

          </div>
        </details>

      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px' }}>
        
        {/* LEFT COLUMN: LIVE BEDSIDE DATA INPUTS */}
        <div>
          <div style={cardStyle}>
            <h3 style={{ color: '#3498db', margin: '0 0 15px 0', fontSize: '1.05rem', textTransform: 'uppercase' }}>
              ⚡ Step 1: Patient Biometric Metrics
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '5px' }}>
              <div>
                <label style={labelStyle}>HEIGHT (CM):</label>
                <input type="number" placeholder="e.g. 181" value={height} onChange={(e) => setHeight(e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>ACTUAL BODY WEIGHT (KG):</label>
                <input type="number" placeholder="e.g. 80" value={weight} onChange={(e) => setWeight(e.target.value)} style={inputStyle} />
              </div>
            </div>
            
            {parseFloat(evaluation.bsa) > 0 && (
              <div style={{ marginTop: '15px', background: '#111416', padding: '12px', borderRadius: '6px', borderLeft: '3px solid #3498db', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.72rem', color: '#bdc3c7' }}>Calculated Body Surface Area (Mosteller):</span>
                <strong style={{ color: '#fff', fontSize: '1.1rem' }}>{evaluation.bsa} <span style={{ fontSize: '0.75rem', fontWeight: 'normal', color: '#95a5a6' }}>m²</span></strong>
              </div>
            )}
          </div>

          <div style={cardStyle}>
            <h3 style={{ color: '#e74c3c', margin: '0 0 15px 0', fontSize: '1.05rem', textTransform: 'uppercase' }}>
              ⏱️ Step 2: Kinetic Time Anchor & Assay Value
            </h3>
            <div style={{ marginBottom: '15px' }}>
              <label style={labelStyle}>POST-INFUSION SAMPLING TIME INTERVAL:</label>
              <select value={samplingHour} onChange={(e) => setSamplingHour(e.target.value)} style={inputStyle}>
                <option value="24">Post 24 Hours after infusion</option>
                <option value="48">Post 48 Hours after infusion</option>
                <option value="72">Post 72 Hours after infusion</option>
                <option value="96">Post 96 Hours after infusion</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>MEASURED SERUM MTX CONCENTRATION (µMOL/L):</label>
              <input 
                type="number" 
                step="0.01" 
                placeholder="Type reported concentration value" 
                value={measuredMtxLevel} 
                onChange={(e) => setMeasuredMtxLevel(e.target.value)} 
                style={{ ...inputStyle, border: measuredMtxLevel ? `1px solid ${evaluation.color}` : '1px solid #34495e' }} 
              />
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN: DYNAMIC RETRIEVAL ANALYSIS TELEMETRY */}
        <div>
          {/* TRIAGE SEVERITY BLOCK HEADER */}
          <div style={{ 
            background: evaluation.bgColor, 
            border: `1px solid ${evaluation.color}`, 
            color: evaluation.color, 
            padding: '15px', 
            borderRadius: '10px', 
            marginBottom: '20px', 
            textAlign: 'center'
          }}>
            <span style={{ fontSize: '0.62rem', display: 'block', letterSpacing: '1px', marginBottom: '2px', opacity: 0.8 }}>SYSTEM EXCITATION TRIAGE STATUS</span>
            <strong style={{ fontSize: '1.2rem', fontWeight: '900', letterSpacing: '0.5px' }}>{evaluation.statusLabel}</strong>
          </div>

          {/* ABSOLUTE DOSE CALCULATION CONTAINER DISPLAY */}
          <div style={{ ...cardStyle, borderLeft: `4px solid ${evaluation.color}`, textAlign: 'center' }}>
            <span style={{ display: 'block', fontSize: '0.72rem', color: '#9b59b6', fontWeight: 'bold', marginBottom: '6px', textTransform: 'uppercase' }}>
              📋 Automated Leucovorin Target Dosing Scale
            </span>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '12px' }}>
              <div style={{ background: '#111416', padding: '12px', borderRadius: '6px' }}>
                <span style={{ fontSize: '0.65rem', color: '#7f8c8d', display: 'block' }}>HANDBOOK BASE UNIT DOSE:</span>
                <strong style={{ fontSize: '1.4rem', color: '#fff' }}>{evaluation.leucovorinUnitDose} <span style={{ fontSize: '0.75rem', fontWeight: 'normal', color: '#bdc3c7' }}>mg/m²</span></strong>
              </div>
              <div style={{ background: '#111416', padding: '12px', borderRadius: '6px', border: evaluation.leucovorinUnitDose > 0 ? '1px solid #9b59b6' : '1px solid transparent' }}>
                <span style={{ fontSize: '0.65rem', color: '#9b59b6', display: 'block', fontWeight: 'bold' }}>PATIENT ABSOLUTE PAYLOAD:</span>
                <strong style={{ fontSize: '1.4rem', color: '#00d2ff' }}>{evaluation.leucovorinAbsoluteDose} <span style={{ fontSize: '0.75rem', fontWeight: 'normal', color: '#bdc3c7' }}>mg</span></strong>
              </div>
            </div>
            {evaluation.leucovorinUnitDose > 0 && (
              <span style={{ fontSize: '0.72rem', color: '#95a5a6', display: 'block', marginTop: '10px', fontWeight: '500' }}>
                👉 Administer: <strong>{evaluation.leucovorinAbsoluteDose} mg IV every 6 hours (QID schedule)</strong>
              </span>
            )}
          </div>

          {/* DISMISSIVE STRATEGY PLAN PANEL CONTAINER */}
          <div style={{ ...cardStyle, borderLeft: '4px solid #3498db' }}>
            <span style={{ display: 'block', fontSize: '0.72rem', color: '#3498db', fontWeight: 'bold', marginBottom: '8px', textTransform: 'uppercase' }}>
              💧 Fluid Supportive Care & Rescue Actions:
            </span>
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#bdc3c7', lineHeight: '1.5', textAlign: 'left' }}>
              {evaluation.actionPlan}
            </p>
          </div>
        </div>

      </div>

      {/* REFERENCE AUDIT FOOTER SCHEDULER BLOCK */}
      <div style={{ marginTop: '20px', padding: '12px', background: '#111416', borderRadius: '8px', border: '1px solid #2c3539', fontSize: '0.65rem', color: '#57606f', lineHeight: '1.4', textAlign: 'left' }}>
        <strong>Calculation Trace Verification:</strong> Mosteller BSA Calculation: √(({height || 0} cm × {weight || 0} kg) / 3600) = {evaluation.bsa} m² | Absolute Dose Formula: {evaluation.leucovorinUnitDose} mg/m² × {evaluation.bsa} m² = {evaluation.leucovorinAbsoluteDose} mg QID.
      </div>

      {/* HANDBOOK DISCLAIMER LEGEND FOOTER FOR MEDICAL FILING */}
      <p style={{ margin: '15px 0 0 0', fontSize: '0.62rem', color: '#7f8c8d', fontStyle: 'italic', textAlign: 'center', lineHeight: '1.3' }}>
        *Evaluation derived from official Clinical Pharmacokinetics Handbook criteria. Patient-specific clinical conditions and absolute biomarker trends must always override automated population estimates prior to final intervention execution.
      </p>

    </div>
  );
}
